import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Pencil, Trash2, Star, StarOff, Users } from 'lucide-react';
import { toast } from 'sonner';
import { employeeEmergencyContactsAPI } from '../lib/api';
import { useCap } from './auth/RequireCap';

// Common-relationship hints. Operator picks one or "Other…" to free-text
// (matches the Other-fallback pattern used elsewhere in the employee form).
// Fix 3 (2026-06-26): Mother / Father / Stepmother / Stepfather added so the
// onboarding-packet relationship value lands on a preset instead of falling
// through to free-text.
const RELATIONSHIP_PRESETS = [
  'Child', 'Domestic Partner', 'Father', 'Friend', 'Mother',
  'Other Relative', 'Parent', 'Sibling', 'Spouse', 'Stepfather', 'Stepmother',
];

const emptyDraft = {
  first_name: '',
  last_name: '',
  relationship: '',
  phone_primary: '',
  phone_secondary: '',
  email: '',
  // Fix 1 (2026-06-26): single-line address. The eForms packet writes the
  // emergency-contact address as one line (`street city, ST zip`) with no
  // reliable delimiter between street and city, so we store + display it
  // verbatim. Legacy `street_address` / `city` / `state` / `zip_code`
  // columns remain in the schema for back-compat but are no longer surfaced
  // in the UI; new entries use this single field.
  address: '',
};

/**
 * Draft mode (employeeId=null): controlled component over `draftContacts` +
 * `onDraftChange`. Parent flushes buffered rows to the server after the
 * employee is created. Live mode (employeeId provided): API-backed CRUD.
 */
export function EmergencyContactsPanel({ employeeId, draftContacts, onDraftChange }) {
  const isDraftMode = !employeeId;
  // Cap gates — match the backend require_cap on each handler. Draft mode
  // keeps controls visible because the user has already passed employees:add
  // at the create-employee gate (write-hole closure 2026-06-26).
  const canAdd = useCap('employees', 'edit');  // mirrors the API: adding a contact is an edit
  const canEdit = useCap('employees', 'edit');
  const canDelete = useCap('employees', 'delete');
  const showAdd = isDraftMode || canAdd;
  const showEdit = isDraftMode || canEdit;
  const showDelete = isDraftMode || canDelete;
  const [liveContacts, setLiveContacts] = useState([]);
  const contacts = isDraftMode ? (draftContacts || []) : liveContacts;
  const setContacts = (next) => {
    if (isDraftMode) {
      onDraftChange && onDraftChange(next);
    } else {
      setLiveContacts(next);
    }
  };

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | contact id (live) or idx-N (draft)
  const [draft, setDraft] = useState(emptyDraft);
  const [otherActive, setOtherActive] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (isDraftMode) return;
    setLoading(true);
    try {
      const r = await employeeEmergencyContactsAPI.list(employeeId);
      setLiveContacts(r.data);
    } catch (e) {
      toast.error('Failed to load emergency contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (employeeId) load(); /* eslint-disable-next-line */ }, [employeeId]);

  const openAdd = () => {
    setEditing('new');
    setDraft({ ...emptyDraft });
    setOtherActive(false);
  };
  const openEdit = (c, idx) => {
    setEditing(isDraftMode ? `idx-${idx}` : c.id);
    setDraft({
      first_name: c.first_name || '',
      last_name: c.last_name || '',
      relationship: c.relationship || '',
      phone_primary: c.phone_primary || '',
      phone_secondary: c.phone_secondary || '',
      email: c.email || '',
      // Prefer the new single-line `address`; if a legacy record only has the
      // structured pieces, join them on the way in so the operator sees their
      // existing value here without losing data.
      address: c.address || [c.street_address, c.city, c.state, c.zip_code].filter(Boolean).join(', '),
    });
    setOtherActive(c.relationship && !RELATIONSHIP_PRESETS.includes(c.relationship));
  };
  const closeForm = () => { setEditing(null); setDraft(emptyDraft); setOtherActive(false); };

  const submit = async () => {
    if (!draft.first_name?.trim() || !draft.last_name?.trim()) { toast.error('First and last name are required'); return; }
    if (!draft.phone_primary?.trim()) { toast.error('Primary phone is required'); return; }
    setSaving(true);
    try {
      if (isDraftMode) {
        if (editing === 'new') {
          const nextList = [...contacts];
          const isPrimary = nextList.length === 0; // first contact auto-primary
          nextList.push({ ...draft, is_primary: isPrimary });
          setContacts(nextList);
          toast.success('Contact queued — will save with the new employee');
        } else {
          const idx = Number(String(editing).replace('idx-', ''));
          setContacts(contacts.map((c, i) => i === idx ? { ...c, ...draft } : c));
          toast.success('Contact updated');
        }
      } else if (editing === 'new') {
        await employeeEmergencyContactsAPI.create(employeeId, draft);
        toast.success('Emergency contact added');
        await load();
      } else {
        await employeeEmergencyContactsAPI.update(employeeId, editing, draft);
        toast.success('Emergency contact updated');
        await load();
      }
      closeForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c, idx) => {
    if (!window.confirm(`Delete emergency contact ${c.first_name} ${c.last_name}?`)) return;
    try {
      if (isDraftMode) {
        const nextList = contacts.filter((_, i) => i !== idx);
        // Promote the oldest remaining as primary if we just removed the primary
        if (c.is_primary && nextList.length > 0 && !nextList.some(x => x.is_primary)) {
          nextList[0] = { ...nextList[0], is_primary: true };
        }
        setContacts(nextList);
        toast.success('Contact removed');
      } else {
        await employeeEmergencyContactsAPI.remove(employeeId, c.id);
        toast.success('Emergency contact deleted');
        await load();
      }
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const setPrimary = async (c, idx) => {
    try {
      if (isDraftMode) {
        setContacts(contacts.map((x, i) => ({ ...x, is_primary: i === idx })));
        toast.success('Primary contact updated');
      } else {
        await employeeEmergencyContactsAPI.setPrimary(employeeId, c.id);
        toast.success('Primary contact updated');
        await load();
      }
    } catch (e) {
      toast.error('Failed to set primary');
    }
  };

  // Relationship dropdown handling — preset or Other… with free-text input
  const relationshipValue = otherActive ? '__other__' : (RELATIONSHIP_PRESETS.includes(draft.relationship) ? draft.relationship : '');

  return (
    <Card data-testid="employee-emergency-contacts-card" className="lg:col-span-3">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Emergency Contacts</CardTitle>
        {showAdd && (
          <Button size="sm" onClick={openAdd} data-testid="employee-emergency-contact-add"><Plus className="w-4 h-4 mr-1" /> Add Contact</Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isDraftMode && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="employee-emergency-contact-draft-hint">
            Contacts you add here are queued in this draft. They&apos;ll be saved when you click <strong>Save Employee</strong>.
          </p>
        )}
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && contacts.length === 0 && editing !== 'new' && (
          showAdd
            ? <p className="text-sm text-slate-500">No emergency contacts on file. Use <strong>Add Contact</strong> to record one.</p>
            : <p className="text-sm text-slate-500" data-testid="employee-emergency-contact-empty-readonly">No emergency contacts on file.</p>
        )}
        {contacts.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Relationship</TableHead>
                <TableHead>Primary Phone</TableHead>
                <TableHead>Secondary</TableHead>
                {(showEdit || showDelete) && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((c, idx) => {
                const rowKey = c.id || `draft-${idx}`;
                return (
                  <TableRow key={rowKey} data-testid={`employee-emergency-contact-row-${rowKey}`}>
                    <TableCell>
                      {c.is_primary ? (
                        <Star className="w-4 h-4 fill-amber-400 stroke-amber-500" aria-label="Primary contact" />
                      ) : showEdit ? (
                        <button onClick={() => setPrimary(c, idx)} title="Set as primary" data-testid={`employee-emergency-contact-make-primary-${rowKey}`}>
                          <StarOff className="w-4 h-4 stroke-slate-300 hover:stroke-amber-500" />
                        </button>
                      ) : (
                        <StarOff className="w-4 h-4 stroke-slate-200" aria-label="Not primary" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{c.first_name} {c.last_name}</div>
                      {c.is_primary && <Badge className="mt-1 bg-amber-100 text-amber-800 hover:bg-amber-100">Primary</Badge>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{c.relationship || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="font-mono text-sm">{c.phone_primary}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{c.phone_secondary || <span className="text-slate-400">—</span>}</TableCell>
                    {(showEdit || showDelete) && (
                      <TableCell className="text-right space-x-1">
                        {showEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(c, idx)} data-testid={`employee-emergency-contact-edit-${rowKey}`}><Pencil className="w-4 h-4" /></Button>}
                        {showDelete && <Button size="sm" variant="ghost" onClick={() => remove(c, idx)} data-testid={`employee-emergency-contact-delete-${rowKey}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {editing && (
          <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50" data-testid="employee-emergency-contact-form">
            <h4 className="font-semibold">{editing === 'new' ? 'Add Emergency Contact' : 'Edit Emergency Contact'}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} data-testid="employee-emergency-contact-first" />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input value={draft.last_name} onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} data-testid="employee-emergency-contact-last" />
              </div>
              <div className="space-y-2">
                <Label>Relationship</Label>
                <Select
                  value={relationshipValue}
                  onValueChange={(v) => {
                    if (v === '__other__') {
                      setOtherActive(true);
                      setDraft({ ...draft, relationship: '' });
                    } else {
                      setOtherActive(false);
                      setDraft({ ...draft, relationship: v });
                    }
                  }}
                >
                  <SelectTrigger data-testid="employee-emergency-contact-relationship"><SelectValue placeholder="Select relationship" /></SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIP_PRESETS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    <SelectItem value="__other__">Other…</SelectItem>
                  </SelectContent>
                </Select>
                {otherActive && (
                  <Input
                    value={draft.relationship}
                    onChange={(e) => setDraft({ ...draft, relationship: e.target.value })}
                    placeholder="Specify relationship"
                    data-testid="employee-emergency-contact-relationship-other"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Primary Phone</Label>
                <Input value={draft.phone_primary} onChange={(e) => setDraft({ ...draft, phone_primary: e.target.value })} className="font-mono" data-testid="employee-emergency-contact-phone-1" />
              </div>
              <div className="space-y-2">
                <Label>Secondary Phone <span className="text-xs text-slate-400">(optional)</span></Label>
                <Input value={draft.phone_secondary} onChange={(e) => setDraft({ ...draft, phone_secondary: e.target.value })} className="font-mono" data-testid="employee-emergency-contact-phone-2" />
              </div>
              <div className="space-y-2">
                <Label>Email <span className="text-xs text-slate-400">(optional)</span></Label>
                <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="employee-emergency-contact-email" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Address <span className="text-xs text-slate-400">(single line — street, city, state, ZIP)</span></Label>
                <Input
                  value={draft.address}
                  onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                  placeholder="e.g. 9700 Skyridge Arcadia, OK 73007"
                  data-testid="employee-emergency-contact-address"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button variant="outline" onClick={closeForm} disabled={saving} data-testid="employee-emergency-contact-cancel">Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="employee-emergency-contact-save">{saving ? 'Saving…' : (editing === 'new' ? 'Add' : 'Save')}</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
